import { Controller } from "@hotwired/stimulus"

// Calculadora de /docs/precios (modelo retail v5). Todo el modelo llega por
// data-precios-calculator-model-value, serializado desde config/precios_beta.yml
// (PreciosBeta#calculator_payload): aquí no vive ningún precio.
//
// Responde UNA pregunta: cuántos créditos consume el proyecto. La plata es otra
// conversación y vive en la escalera de abajo (acuerdo del standup 09-16:
// «tú enfócate en los créditos y yo me enfoco en la plata»).
//
// Replica PreciosBeta.quote / once_centi / project_centi / schedule_cost, en
// CÉNTIMOS de crédito enteros (24.72 × 500 en flotante da 12360.000000000002 →
// 12,361):
//   por corrida y entidad = una llamada de detalle (un cargo base; dentro de la
//                           llamada se cobra lo que `resolve_codes` del gateway
//                           cobraría: greedy por tags, ver quote())
//                         + una llamada por cada SKU de endpoint propio
//   una vez por entidad   = resolver identidad, si no se tiene el ID de Panadata:
//                           DAT-RESOLVE por fila, en lotes de resolveBatchMax
//                           filas que pagan UN cargo base cada uno (onceCenti)
//   proyecto              = por corrida × entidades × corridas + resolver la lista
// El plan se recomienda con lo que de verdad se puede comprar (recargas de $95,
// meses de suscripción, recargas anuales) y con el calendario real: un barrido
// único necesita sus créditos en UN mes.
export default class extends Controller {
  static targets = [
    "recipe", "customChip", "sku", "skuRow", "recipeNote",
    "volume", "slider", "freq", "hasId",
    "total", "sub", "perRun", "onceRow", "once", "entities", "runs", "runTotal", "monthly",
    "planRec", "ventas", "breakdown", "rung", "mobileTotal", "mobilePlan"
  ]
  static values = { model: Object }

  static CHIP_ON = ["border-panadata-primary", "bg-panadata-primary-light", "text-panadata-primary-dark"]
  static CHIP_OFF = ["border-gray-200", "bg-white", "text-gray-600"]
  static RUNG_ON = ["bg-panadata-primary-light/30"]

  connect() {
    this.skusByCode = Object.fromEntries(this.modelValue.skus.map((s) => [s.code, s]))
    this.selected = new Set(this.skuTargets.filter((i) => i.checked).map((i) => i.dataset.sku))
    this.n = this.clampVolume(this.volumeTarget.value)
    this.runs = Number(this.freqTargets.find((b) => b.dataset.on === "true")?.dataset.runs || 1)
    // «Personalizado» explícito: el chip se queda encendido aunque no haya nada marcado.
    this.custom = false
    this.update()
  }

  // ── Entradas ──

  pickRecipe(event) {
    const recipe = this.modelValue.recipes.find((r) => r.id === event.currentTarget.dataset.recipe)
    if (!recipe) return
    this.custom = false
    this.selected = new Set(recipe.skus)
    // La casilla del ID manda: la receta lista el caso completo y aquí se le
    // quita resolver si el usuario ya declaró que tiene el ID de Panadata.
    if (this.hasIdTarget.checked) this.selected.delete(this.resolveCode())
    this.syncCheckboxes()
    this.update()
  }

  pickCustom() {
    // Lienzo en blanco: se limpia la receta, el chip se enciende y el foco va al
    // primer SKU para marcar. Resolver se conserva si el usuario declaró que no
    // tiene el ID, para no desincronizar la casilla (toggleId/toggleSku la atan).
    this.custom = true
    this.selected = new Set()
    if (!this.hasIdTarget.checked) this.selected.add(this.resolveCode())
    this.syncCheckboxes()
    this.skuTargets[0]?.focus()
    this.update()
  }

  toggleSku(event) {
    const input = event.currentTarget
    if (input.checked) this.selected.add(input.dataset.sku)
    else this.selected.delete(input.dataset.sku)
    // Marcar resolver a mano equivale a decir que no se tiene el ID.
    if (input.dataset.sku === this.resolveCode()) this.hasIdTarget.checked = !input.checked
    this.syncCheckboxes()
    this.update()
  }

  toggleId(event) {
    if (event.currentTarget.checked) this.selected.delete(this.resolveCode())
    else this.selected.add(this.resolveCode())
    this.syncCheckboxes()
    this.update()
  }

  pickFreq(event) {
    this.runs = Number(event.currentTarget.dataset.runs)
    this.freqTargets.forEach((b) => this.paintChip(b, b === event.currentTarget))
    this.update()
  }

  typeVolume(event) {
    this.n = this.clampVolume(event.currentTarget.value)
    this.sliderTarget.value = Math.round((Math.log10(this.n) / this.logSpan()) * 1000)
    this.update()
  }

  // Al salir del campo, lo tecleado se normaliza a lo que se calculó (100001 →
  // 100000, vacío → 1): el número que se ve es el número que se cotizó.
  commitVolume() {
    this.volumeTarget.value = this.n
  }

  slideVolume(event) {
    let n = Math.round(Math.pow(10, (Number(event.currentTarget.value) / 1000) * this.logSpan()))
    // Imanes en los valores redondos que la gente busca.
    if (n >= 96 && n <= 104) n = 100
    if (n >= 480 && n <= 520) n = 500
    if (n >= 960 && n <= 1040) n = 1000
    this.n = this.clampVolume(n)
    this.volumeTarget.value = this.n
    this.update()
  }

  // ── Modelo (espejo de PreciosBeta) ──

  centi(credits) {
    return Math.round(credits * 100)
  }

  // Espejo de PreciosBeta.quote: los SKUs de la llamada de detalle se ordenan
  // por cantidad de tags (mayor primero; empate: orden del catálogo) y cada uno
  // se cobra solo si aporta un tag que los anteriores no cubrían ya entre todos.
  quote() {
    const m = this.modelValue
    const codes = [...this.selected].filter((c) => this.skusByCode[c])
    const resolve = codes.includes(this.resolveCode())
    const rows = codes.filter((c) => c !== this.resolveCode()).map((c) => this.skusByCode[c])
    const detail = rows.filter((s) => s.call !== "own")
    const own = rows.filter((s) => s.call === "own")

    const order = [...detail].sort((a, b) => (b.tags.length - a.tags.length) || (a.order - b.order))
    const covered = new Set()
    const charged = []
    const included = {}
    order.forEach((s) => {
      if (s.tags.every((t) => covered.has(t))) {
        included[s.code] = charged.filter((c) => c.tags.some((t) => s.tags.includes(t))).map((c) => c.code)
      } else {
        s.tags.forEach((t) => covered.add(t))
        charged.push(s)
      }
    })
    const requests = (detail.length ? 1 : 0) + own.length
    const sum = (list) => list.reduce((t, s) => t + this.centi(s.credits), 0)

    return {
      perRun: sum(charged) + sum(own) + requests * this.centi(m.baseFee),
      once: resolve ? this.centi(this.skusByCode[this.resolveCode()].credits) : 0,
      requests, charged, own, included, resolve,
      selected: rows.length + (resolve ? 1 : 0)
    }
  }

  // Espejo de PreciosBeta.resolve_calls / once_centi: la lista se resuelve en
  // lotes llenos de POST /v5/resolve/batch, un cargo base por lote.
  resolveCalls() {
    return Math.ceil(this.n / this.modelValue.resolveBatchMax)
  }

  onceCenti(q) {
    return q.resolve ? q.once * this.n + this.resolveCalls() * this.centi(this.modelValue.baseFee) : 0
  }

  projectCenti(q) {
    return q.perRun * this.n * this.runs + this.onceCenti(q)
  }

  // Espejo de PreciosBeta.schedule_cost, en céntimos enteros. Lo que se puede
  // comprar: recargas PAYG de $95 (95 cr), `months` meses seguidos de
  // suscripción (por defecto 1 si el proyecto es un solo barrido, 12 si se
  // repite; las corridas se reparten sobre los 12 meses del año igual) y
  // recargas anuales. El saldo acumula entre meses mientras creditsRollOver sea
  // true. `firstPayment` es el efectivo de hoy: primer mes o recarga anual más
  // las `firstPacks` recargas que pide la primera corrida (#268).
  scheduleCost(plan, perRun, runs, once, months = null) {
    const m = this.modelValue
    const payg = m.plans.find((p) => p.cadence === "once")
    const totalC = perRun * runs + once
    const grantedC = plan.credits * 100
    const packC = payg.credits * 100
    const firstC = perRun + once
    const out = { granted: plan.credits, packCredits: payg.credits, firstRunNeed: firstC / 100 }

    if (plan.cadence === "once") {
      const packs = Math.ceil(totalC / packC)
      const firstPacks = Math.ceil(firstC / packC)
      return { ...out, year: packs * payg.commitment, months: 0, packs, firstRunShortfall: 0, shortfallPerRun: 0,
        shortfallTotal: 0, firstPacks, firstPayment: firstPacks * payg.commitment, leftover: (packs * packC - totalC) / 100 }
    }
    if (plan.cadence === "monthly") {
      if (months === null) months = runs <= 1 ? 1 : 12
      const runMonths = Array.from({ length: runs }, (_, k) => 1 + Math.round((k * 12) / runs))
      let balance = 0
      const shortfalls = []
      for (let mo = 1; mo <= 12; mo++) {
        if (mo <= months) balance += grantedC
        runMonths.filter((x) => x === mo).forEach(() => {
          const need = shortfalls.length === 0 ? firstC : perRun
          shortfalls.push(Math.max(0, need - balance))
          balance = Math.max(0, balance - need)
        })
        if (!m.creditsRollOver) balance = 0
      }
      const shortfallC = shortfalls.reduce((a, b) => a + b, 0)
      const packs = Math.ceil(shortfallC / packC)
      const firstPacks = Math.ceil((shortfalls[0] || 0) / packC)
      return { ...out, year: plan.commitment * months + packs * payg.commitment, months, packs,
        firstRunShortfall: (shortfalls[0] || 0) / 100, shortfallPerRun: Math.max(0, ...shortfalls.slice(1)) / 100,
        shortfallTotal: shortfallC / 100, firstPacks, firstPayment: plan.commitment + firstPacks * payg.commitment,
        leftover: (grantedC * months + packs * packC - totalC) / 100 }
    }
    let recharges = Math.max(1, Math.floor(totalC / grantedC))
    const restC = Math.max(0, totalC - recharges * grantedC)
    let packs = Math.ceil(restC / packC)
    if (restC > 0 && (recharges + 1) * plan.commitment <= recharges * plan.commitment + packs * payg.commitment) {
      recharges += 1
      packs = 0
    }
    const shortfallC = Math.max(0, totalC - grantedC)
    const firstPacks = packs === 0 ? 0 : Math.ceil(Math.max(0, firstC - grantedC) / packC)
    return { ...out, year: recharges * plan.commitment + packs * payg.commitment, months: 12, packs, recharges,
      firstRunShortfall: Math.max(0, firstC - grantedC) / 100, shortfallPerRun: shortfallC / 100,
      shortfallTotal: shortfallC / 100, firstPacks, firstPayment: plan.commitment + firstPacks * payg.commitment,
      leftover: (recharges * grantedC + packs * packC - totalC) / 100 }
  }

  // Espejo de PreciosBeta.prepaid_alternative: pagar sólo lo que hace falta
  // (de 1 a 12 meses de un mensual y cancelar, la anual, o recargas sueltas)
  // cuando sale más barato que el calendario recomendado. null si no hay
  // corridas repetidas o no ahorra. Empate: orden del catálogo, luego menos meses.
  prepaidAlternative(perRunAll, onceAll, recommendedYear) {
    if (this.runs <= 1) return null
    const m = this.modelValue
    let best = null
    m.plans.forEach((plan, i) => {
      const durations = plan.cadence === "monthly" ? Array.from({ length: 12 }, (_, k) => k + 1) : [null]
      durations.forEach((months) => {
        const cost = this.scheduleCost(plan, perRunAll, this.runs, onceAll, months)
        if (!best || cost.year < best.cost.year - 1e-9) best = { plan, cost, i, months }
      })
    })
    return best && best.cost.year < recommendedYear - 1e-9 ? { plan: best.plan, cost: best.cost } : null
  }

  update() {
    const m = this.modelValue
    const q = this.quote()
    const perRunAll = q.perRun * this.n
    const onceAll = this.onceCenti(q)
    const costs = Object.fromEntries(m.plans.map((p) => [p.id, this.scheduleCost(p, perRunAll, this.runs, onceAll)]))
    const suggested = m.plans.reduce((best, p) => (costs[p.id].year < costs[best.id].year - 1e-9 ? p : best), m.plans[0])
    const prepaid = this.prepaidAlternative(perRunAll, onceAll, costs[suggested.id].year)

    this.paintRecipes()
    this.paintSkus(q)
    this.paintRungs(suggested.id)
    this.paintResult({ q, project: this.projectCenti(q), perRunAll, onceAll, suggested, best: costs[suggested.id], prepaid })
  }

  // ── Pintado ──

  paintRecipes() {
    // La receta casa con lo marcado mirando solo lo facturable: con el ID en
    // mano, «Due diligence» sigue siendo Due diligence aunque falte resolver.
    const billable = (codes) => (this.hasIdTarget.checked ? codes.filter((c) => c !== this.resolveCode()) : codes)
    const match = this.modelValue.recipes.find((r) => {
      const want = billable(r.skus)
      return want.length === this.selected.size && want.every((c) => this.selected.has(c))
    })
    this.recipeTargets.forEach((chip) => this.paintChip(chip, !!match && chip.dataset.recipe === match.id))
    this.paintChip(this.customChipTarget, !match && (this.selected.size > 0 || this.custom))
    this.recipeNoteTarget.textContent = match
      ? match.contiene
      : this.selected.size === 0
        ? "Marca al menos un SKU para ver qué consume tu proyecto."
        : "Selección personalizada: abajo está lo que trae cada SKU que marcaste."
  }

  paintSkus(q) {
    this.skuRowTargets.forEach((row) => {
      const by = q.included[row.dataset.sku]
      row.classList.toggle("opacity-40", !!by)
      row.title = by ? `Ya ${this.includedLabel(by)} (${by.join(" + ")}): no se cobra` : ""
    })
  }

  // «incluido en Quién es» o «cubierto entre Quién es y Riesgo local».
  includedLabel(by) {
    const names = by.map((c) => this.skusByCode[c].name)
    if (names.length === 1) return `incluido en ${names[0]}`
    return `cubierto entre ${names.slice(0, -1).join(", ")} y ${names[names.length - 1]}`
  }

  paintRungs(suggestedId) {
    this.rungTargets.forEach((r) => {
      r.classList.remove(...this.constructor.RUNG_ON)
      if (r.dataset.plan === suggestedId) r.classList.add(...this.constructor.RUNG_ON)
    })
  }

  paintResult({ q, project, perRunAll, onceAll, suggested, best, prepaid }) {
    const nf = new Intl.NumberFormat("en-US")
    const ceil = (c) => Math.floor((c + 99) / 100)
    const cr = (c) => `${(c / 100).toFixed(2)} cr`
    const money = (x, dec = x >= 1000 ? 0 : 2) =>
      "$" + new Intl.NumberFormat("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec }).format(x)
    const plural = (n, one, many) => `${nf.format(n)} ${n === 1 ? one : many}`

    if (q.selected === 0) {
      this.totalTarget.textContent = "—"
      this.subTarget.textContent = "Marca al menos un SKU."
      ;[this.perRunTarget, this.onceTarget, this.entitiesTarget, this.runsTarget, this.runTotalTarget, this.monthlyTarget]
        .forEach((t) => (t.textContent = "—"))
      this.onceRowTarget.hidden = true
      this.planRecTarget.textContent = ""
      this.ventasTarget.hidden = true
      this.breakdownTarget.textContent = ""
      this.paintMobile("—", "")
      return
    }

    this.totalTarget.innerHTML = ""
    this.totalTarget.append(nf.format(ceil(project)))
    const unit = document.createElement("span")
    unit.className = "text-base font-semibold tracking-normal text-gray-400"
    unit.textContent = " créditos"
    this.totalTarget.append(unit)

    this.subTarget.textContent =
      `${plural(this.n, "entidad", "entidades")} × ${plural(this.runs, "corrida", "corridas")} al año` +
      (q.resolve ? ", resolviendo la lista una vez" : "")

    this.perRunTarget.textContent = cr(q.perRun)
    this.onceRowTarget.hidden = !q.resolve
    this.onceTarget.textContent = cr(q.once)
    this.entitiesTarget.textContent = nf.format(this.n)
    this.runsTarget.textContent = String(this.runs)
    this.runTotalTarget.textContent = `${nf.format(ceil(perRunAll))} cr`
    this.monthlyTarget.textContent = `${nf.format(ceil(Math.ceil(project / 12)))} cr`

    // La recomendación: misma forma que docs/_precios_plan_rec.html.erb.
    this.planRecTarget.innerHTML = ""
    const lead = document.createElement("span")
    lead.className = "font-semibold text-panadata-charcoal"
    lead.textContent =
      `Con ${nf.format(ceil(perRunAll))} créditos por corrida, ${plural(this.runs, "corrida", "corridas")} al año, ` +
      `el plan que te conviene es ${suggested.name}: ${money(best.year)} al año.`
    this.planRecTarget.append(lead)
    const payg = this.modelValue.plans.find((p) => p.cadence === "once")
    const recargas = (n) => `${plural(n, "recarga", "recargas")} de ${money(payg.commitment, 0)}`
    const parts = []
    if (suggested.cadence === "once") {
      parts.push(`Son ${recargas(best.packs)} (${nf.format(best.packCredits)} cr cada una): cada recarga es una compra aparte en Facturación.`)
      if (best.leftover > 0) parts.push(`Te sobran ${nf.format(Math.floor(best.leftover))} cr, que ${suggested.validity}.`)
    } else if (suggested.cadence === "monthly") {
      parts.push(
        `${suggested.name} trae ${nf.format(best.granted)} cr al mes por ${money(suggested.commitment, 0)}: ` +
        (best.months === 1
          ? `un mes de suscripción basta, y se cancela desde el portal de facturación.`
          : `${best.months} meses de suscripción (${money(suggested.commitment * best.months)}).`)
      )
      if (best.firstRunShortfall > 0) {
        parts.push(
          `La primera corrida necesita ${nf.format(Math.ceil(best.firstRunNeed))} cr y el plan aporta ${nf.format(best.granted)}: ` +
          `te faltan ${nf.format(Math.ceil(best.firstRunShortfall))} cr antes de correr.`
        )
      }
      if (this.runs > 1) {
        parts.push(best.shortfallPerRun > 0
          ? `A cada corrida siguiente le faltan ${nf.format(Math.ceil(best.shortfallPerRun))} cr.`
          : `Las corridas siguientes caben en lo que el plan acumula.`)
      }
      if (best.packs > 0) parts.push(`Lo que falta se compra como ${recargas(best.packs)} en el año (${money(best.packs * payg.commitment)}).`)
      if (this.modelValue.creditsRollOver) {
        parts.push(best.leftover > 0
          ? `Te sobran ${nf.format(Math.floor(best.leftover))} cr, que se quedan en tu saldo.`
          : "Los créditos que no uses se quedan en tu saldo.")
      }
    } else {
      parts.push(`La recarga anual trae ${nf.format(best.granted)} cr por ${money(suggested.commitment, 0)}.`)
      if (best.recharges > 1) parts.push(`El año consume ${nf.format(ceil(project))} cr: son ${plural(best.recharges, "recarga anual", "recargas anuales")} (${money(best.year)}).`)
      if (best.packs > 0) parts.push(`Lo que falta se compra como ${recargas(best.packs)}.`)
      if (best.leftover > 0) parts.push(`Te sobran ${nf.format(Math.floor(best.leftover))} cr, que ${suggested.validity}.`)
    }
    if (prepaid) {
      const pp = prepaid.plan, pc = prepaid.cost
      const extra = pc.packs > 0 ? ` más ${recargas(pc.packs)}` : ""
      const compra = pp.cadence === "once"
        ? recargas(pc.packs)
        : pp.cadence === "monthly"
          ? `${pc.months === 1 ? "un solo mes" : `${pc.months} meses`} de ${pp.name} (${money(pp.commitment * pc.months)})${extra}`
          : `${plural(pc.recharges, "recarga anual", "recargas anuales")} (${money(pp.commitment * pc.recharges)})${extra}`
      const hoy = pp.cadence === "once"
        ? `las ${recargas(pc.firstPacks)} de la primera corrida`
        : pp.cadence === "monthly"
          ? "el primer mes" + (pc.firstPacks > 0 ? ` y ${recargas(pc.firstPacks)}` : "")
          : "la recarga anual" + (pc.firstPacks > 0 ? ` y ${recargas(pc.firstPacks)}` : "")
      const compras = pc.packs + (pp.cadence === "monthly" ? 1 : pp.cadence === "once" ? 0 : pc.recharges)
      parts.push(
        `Pagar el año por adelantado sale más barato: ${compra} cubre las ${this.runs} corridas por ${money(pc.year)} en total, porque el saldo se queda en tu cuenta` +
        (pp.cadence === "monthly" ? " y la suscripción se cancela desde el portal de facturación." : ".")
      )
      if (pc.firstPayment < pc.year) parts.push(`Hoy pagas ${money(pc.firstPayment)} (${hoy}); el resto, cuando toque.`)
      parts.push(pc.packs > 0
        ? "Ojo: hoy cada recarga es una compra aparte en Facturación" + (compras > 1 ? ` (${compras} compras)` : "") +
          ", y la recomendación de arriba es la forma cómoda de trabajar, no la más barata."
        : "La recomendación de arriba es la forma cómoda de trabajar, no la más barata.")
    }
    parts.push("Cifras de referencia: el precio final lo fija el compromiso que elijas en Facturación. La escalera completa está abajo.")
    this.planRecTarget.append(" " + parts.join(" "))

    this.ventasTarget.hidden = best.year <= this.modelValue.enterprise.threshold

    this.breakdownTarget.innerHTML = ""
    const title = document.createElement("p")
    title.className = "mb-1 text-xs font-semibold text-gray-500"
    title.textContent = "Por cada entidad y corrida"
    this.breakdownTarget.append(title)
    const row = (label, creditsText, muted) => {
      const div = document.createElement("div")
      div.className = "grid grid-cols-[1fr_auto] gap-2.5" + (muted ? " text-gray-400" : "")
      const a = document.createElement("span"); a.textContent = label
      const b = document.createElement("span")
      b.className = muted ? "font-mono tabular-nums" : "font-mono font-semibold tabular-nums text-panadata-charcoal"
      b.textContent = creditsText
      div.append(a, b)
      return div
    }
    q.charged.forEach((s) => this.breakdownTarget.append(row(s.name, cr(this.centi(s.credits)), false)))
    Object.entries(q.included).forEach(([code, by]) =>
      this.breakdownTarget.append(row(`${this.skusByCode[code].name} · ${this.includedLabel(by)}`, "0.00 cr", true))
    )
    q.own.forEach((s) => this.breakdownTarget.append(row(`${s.name} · llamada propia`, cr(this.centi(s.credits)), false)))
    this.breakdownTarget.append(
      row(`Cargo base × ${plural(q.requests, "solicitud", "solicitudes")}`, cr(q.requests * this.centi(this.modelValue.baseFee)), true)
    )
    if (q.resolve) {
      const p = document.createElement("p")
      p.className = "mb-1 mt-2 text-xs font-semibold text-gray-500"
      p.textContent = "Una sola vez por entidad"
      this.breakdownTarget.append(p)
      const lotes = this.resolveCalls()
      this.breakdownTarget.append(row(`Resolver identidad · en lotes de hasta ${nf.format(this.modelValue.resolveBatchMax)}`, cr(q.once), false))
      this.breakdownTarget.append(
        row(`Cargo base × ${plural(lotes, "lote", "lotes")}, por toda la lista`, cr(lotes * this.centi(this.modelValue.baseFee)), true)
      )
    }

    this.paintMobile(`${nf.format(ceil(project))} cr`, suggested.name)
  }

  paintMobile(total, plan) {
    if (this.hasMobileTotalTarget) this.mobileTotalTarget.textContent = total
    if (this.hasMobilePlanTarget) this.mobilePlanTarget.textContent = plan
  }

  // ── Utilidades ──

  syncCheckboxes() {
    this.skuTargets.forEach((i) => (i.checked = this.selected.has(i.dataset.sku)))
  }

  paintChip(el, on) {
    el.dataset.on = on ? "true" : "false"
    el.setAttribute("aria-pressed", on ? "true" : "false")
    el.classList.remove(...(on ? this.constructor.CHIP_OFF : this.constructor.CHIP_ON))
    el.classList.add(...(on ? this.constructor.CHIP_ON : this.constructor.CHIP_OFF))
  }

  resolveCode() {
    return this.modelValue.resolveCode
  }

  clampVolume(value) {
    const n = Math.round(Number(value) || 1)
    const clamped = Math.max(1, Math.min(this.modelValue.maxVolume, n))
    if (this.hasSliderTarget) {
      this.sliderTarget.setAttribute("aria-valuenow", String(clamped))
      this.sliderTarget.setAttribute("aria-valuetext", `${new Intl.NumberFormat("en-US").format(clamped)} entidades`)
    }
    return clamped
  }

  logSpan() {
    return Math.log10(this.modelValue.maxVolume)
  }
}
